import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { X, Sparkles, ShieldCheck, AlertCircle, RefreshCw, CheckCircle2, ArrowRight } from 'lucide-react';

interface SandboxPromoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (promotedCount: number) => void;
}

export const SandboxPromoteModal: React.FC<SandboxPromoteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tenant, promoteSandbox } = useAuth();
  const [isPromoting, setIsPromoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen) return null;

  const handlePromote = async () => {
    if (!confirmed) {
      setError('Kérjük, pipáld be a megerősítő jelölőnégyzetet az átvételhez!');
      return;
    }
    setIsPromoting(true);
    setError(null);
    const res = await promoteSandbox();
    setIsPromoting(false);
    if (res.ok) {
      onSuccess(res.promoted_count || 0);
    } else {
      setError(res.error || 'Hiba történt az adatok átvétele során.');
    }
  };

  const cleanTenantName = (tenant?.name || '').replace(/^\[DEMO\]\s*/i, '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 shadow-2xl space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/60">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base text-slate-900 dark:text-white">
                Tartalom Átvétele az Élesbe
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Homokozó (DEMO) adatok élesítése a hivatalos nyilvántartásba
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Környezetek vizuális kapcsolata */}
        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80">
          <div className="flex items-center justify-between gap-3 text-xs">
            <div className="flex-1 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/40">
              <span className="text-[10px] uppercase font-bold text-amber-800 dark:text-amber-300 block mb-1">
                Forrás (Teszt)
              </span>
              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate block">
                {tenant?.name || 'Homokozó'}
              </span>
            </div>

            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />

            <div className="flex-1 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/40">
              <span className="text-[10px] uppercase font-bold text-emerald-800 dark:text-emerald-300 block mb-1">
                Cél (Éles)
              </span>
              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate block">
                {cleanTenantName}
              </span>
            </div>
          </div>
        </div>

        {/* Információs doboz */}
        <div className="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/40 text-xs text-blue-900 dark:text-blue-200 space-y-1.5 leading-relaxed">
          <div className="font-bold flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <span>Biztonságos Átvételezési Folyamat</span>
          </div>
          <p>
            A homokozóban létrehozott és szerkesztett bejegyzések átkerülnek az éles szervezet megfelelő közzétételi oldalaihoz. Az éles szervezet egyéb, nem érintett adatai sértetlenek maradnak.
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Megerősítő jelölőnégyzet */}
        <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition cursor-pointer select-none text-xs text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 rounded text-amber-600 focus:ring-amber-500 h-4 w-4 border-slate-300 dark:border-slate-700"
          />
          <span>
            Megerősítem, hogy ellenőriztem a homokozóban lévő adatokat, és jóváhagyom azok éles közzétételi adatbázisba történő átvételét.
          </span>
        </label>

        {/* Gombok */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPromoting}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            Mégse
          </button>
          <button
            type="button"
            onClick={handlePromote}
            disabled={isPromoting || !confirmed}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center gap-1.5 transition cursor-pointer shadow-md"
          >
            {isPromoting ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Átvétel folyamatban...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Átvétel Végrehajtása az Élesbe</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
